import { useEffect } from 'react';

import { Button } from '@mui/material';
import Container from '@mui/material/Container';

import { paths } from 'src/routes/paths';
import { RouterLink } from 'src/routes/components';

import { getKitchenDispatchOrderById } from 'src/redux/slices/kitchen-dispatch-order';

import ErrorContent from 'src/components/error-content';
import { useSettingsContext } from 'src/components/settings';
import { LeftIcon } from 'src/components/carousel/arrow-icons';
import CustomBreadcrumbs from 'src/components/custom-breadcrumbs';

import { LoadingScreen } from '../../../components/loading-screen';
import { useAppDispatcher, useAppSelector } from '../../../redux/store';
import KitchenDispatchOrderDetails from '../kitchen-dispatch-order-details';

type Props = {
  id: string;
};

export default function KitchenDispatchOrderDetailView({ id }: Props) {
  const { item, status, error } = useAppSelector((state) => state.kitchenDispatchOrders);

  const settings = useSettingsContext();
  const dispatch = useAppDispatcher();

  useEffect(() => {
    dispatch(getKitchenDispatchOrderById({ itemID: id }));
  }, [dispatch, id]);

  if (status === 'loading' || status === 'idle') {
    return <LoadingScreen />;
  }

  if (status === 'failure') {
    return (
      <ErrorContent
        filled
        title="Failed to load kitchen dispatch order details"
        description={error}
        action={
          <Button
            component={RouterLink}
            href={paths.dashboard.kitchen.dispatch.root()}
            variant="contained"
            startIcon={<LeftIcon />}
          >
            Back to Kitchen Dispatch Orders
          </Button>
        }
        sx={{
          py: 10,
        }}
      />
    );
  }

  if (status === 'success' && item) {
    return (
      <Container maxWidth={settings.themeStretch ? false : 'lg'}>
        <CustomBreadcrumbs
          heading="Kitchen Dispatch Order Details"
          links={[
            {
              name: 'Kitchen Dispatch Orders',
              href: paths.dashboard.kitchen.dispatch.root(),
            },
            { name: 'Kitchen Dispatch Order Details' },
          ]}
          sx={{
            mb: { xs: 3, md: 5 },
          }}
        />

        <KitchenDispatchOrderDetails currentKitchenDispatchOrder={item} />
      </Container>
    );
  }

  return <span />;
}
